<template>
	<section class="events" data-testid="events-component">
		<div class="panel-wrapper hidden-print">
			<div class="container">
				<div class="page-head page-head-compact">
					<button class="btn btn-primary btn-xs" data-testid="filtersBtn" data-toggle="collapse" data-target="#filtersSub" aria-controls="filtersSub" data-label-collapsed="Filters" data-label-expanded="Hide Filters" aria-expanded="false">Filters</button>
				</div>
			</div>
		</div>

		<!-- Filters -->
		<SearchFilters
			v-if="facetData"
			filterPage="events"
			id="filtersSub"
			ref="filtersSub"
			:filterTypes="filterTypes"
			:facets="facetData"
			:queryFilters="queryFilters"
			:savedSearchFilters="[]"
			prefix="ltf-events-filter-"
			@changed="handleFiltersChanged"
			@rebuildQueryFilters="handleRebuildQueryFilters"
			class="collapse"
			itemCssClass="col-xs-12 col-md-6 col-lg-4"
		/>

		<!-- Chiclets -->
		<Chiclets
			v-if="events"
			:selected="searchChiclets"
			:savedSearchFilters="savedSearchFilters"
			:allowFavorites="false"
			@removed="handleRemovedChiclet"
			@clearAllChiclets="handleClearAllChiclets"
		/>

		<!-- Events List -->
		<div class="events-container">
			<div class="container">
				<div class="row">
					<div :class="{ 'col-xs-12 col-lg-9': nearbyEventsVisible, 'col-xs-12 col-lg-8 col-lg-offset-2': !nearbyEventsVisible }">
						<a v-for="(event, eventIdx) in events" class="card card-event" :class="{ 'is-featured': event.featured }" :key="eventIdx" :href="event.url">
							<div class="card-header" :style="{ 'background-image': event.imageUrl }">
								<p class="card-date">
									<time class="sr-only" :datetime="event.start">{{ event.dateFull }}</time>
									<time class="time-start" :datetime="event.start" aria-hidden="true">
										<span class="event-day">{{ event.dateDayOfWeekAbbr }}</span>
										<span class="event-date">{{ event.dateDayOfMonth }}</span>
										<span class="event-month">{{ event.dateMonthAbbr }}</span>
									</time>
								</p>
							</div>
							<div class="card-block">
								<p class="card-title">{{ event.title }}</p>
								<p class="card-text" v-if="event.featured">{{ event.description }}</p>
								<p class="card-time"><span class="ico-clock-sm"></span><TemporalDisplay :start="event.startMoment" :end="event.endMoment" template="time"/></p>
								<p class="card-location"><span class="ico-map-marker-sm"></span>{{ event.location }}</p>
								<p class="card-price" v-if="event.isPaidClass"><span class="ico-cost-sm"></span>Paid Event</p>
								<p class="card-register" v-if="event.isRegistrable"><span class="ico-calendar-checked"></span>Reserve</p>
							</div>
						</a>

						<!-- No Events -->
						<div class="card card-event is-empty" v-if="!events.length && show">
							<p class="card-text">There are no events that match the current criteria. Please select a different set of filters.</p>
						</div>
					</div>
					<!-- Nearby Events -->
					<aside class="events-aside col-xs-12 col-lg-3" v-if="!isWorkExperience">
						<div v-if="show && nearbyEvents.length">
							<h2 class="h4 section-header">Events Near {{ club }} </h2>
							<a v-for="(event, eventIdx) in nearbyEvents" :key="eventIdx" class="card card-event" :href="event.url" :class="{ 'is-featured': event.featured }">
								<div class="card-header" :style="{ 'background-image': event.imageUrl }">
									<p class="card-date">
										<time class="sr-only" :datetime="event.start">{{ event.dateFull }}</time>
										<time class="time-start" :datetime="event.start" aria-hidden="true">
											<span class="event-day">{{ event.dateDayOfWeekAbbr }}</span>
											<span class="event-date">{{ event.dateDayOfMonth }}</span>
											<span class="event-month">{{ event.dateMonthAbbr }}</span>
										</time>
									</p>
								</div>
								<div class="card-block">
									<p class="card-title">{{ event.title }}</p>
									<p class="card-text" v-if="event.featured">{{ event.description }}</p>
									<p class="card-time"><span class="ico-clock-sm"></span><TemporalDisplay :start="event.startMoment" :end="event.endMoment" template="time"/></p>
									<p class="card-location"><span class="ico-map-marker-sm"></span>{{ event.location }}</p>
									<p class="card-location">{{ event.distance }} miles</p>
									<p class="card-price" v-if="event.isPaidClass"><span class="ico-cost-sm"></span>Paid Event</p>
									<p class="card-register" v-if="event.isRegistrable"><span class="ico-calendar-checked"></span>Reserve</p>
								</div>
							</a>
						</div>
					</aside>
				</div>
			</div>
		</div>

		<!-- Loading Spinner -->
		<div role="alert" aria-live="assertive">
			<transition name="fade" appear>
				<div class="spinner-overlay" data-testid="spinner" v-show="isWaiting">
					<p class="sr-only">Content is loading…</p>
					<LoadingSpinner />
				</div>
			</transition>
		</div>
	</section>
</template>

<script>
import { defineComponent } from 'vue';
import _ from 'lodash';
import moment from 'moment';
import LoadingSpinner from '@/components/schreg-common/LoadingSpinner.vue';
import { baseComponentMixin } from '@/mixins/baseComponent.mixin';
import { UxSchedulesApi } from '@/apis/uxSchedules.api';
import { FrameworkService } from '@/services/framework.service';
import TemporalDisplay from '@/components/schreg-common/TemporalDisplay.vue';
import { FilterTypes } from '@/services/filtering.service.config';
import SearchFilters from '@/components/schreg-common/SearchFilters.vue';
import Chiclets from '@/components/schreg-common/Chiclets.vue';
import { StateService } from '@/services/state.service.js';
import { FilteringService, FilteringServicePages } from '@/services/filtering.service';

export default defineComponent({
	name: 'EventsComponent',
	config: {
		key: 'schreg.events',
		devPath: 'events-list',
		exported: true
	},
	components:{
		LoadingSpinner,
		SearchFilters,
		Chiclets,
		TemporalDisplay
	},
	mixins : [
		baseComponentMixin
	],
	data() {
		return {
			events: [],
			show: false,
			facetData: null,
			filterTypes: [],
			savedSearchFilters: [],
			searchChiclets: [],
			queryFilters: [],
			searchFilters: {},
			club: null,
			nearbyEvents: [],
			nearbyEventsVisible: false,
			isWorkExperience: FrameworkService.isWorkExperience()
		};
	},
	mounted() {
		const stores = FrameworkService.isWorkExperience()
			? ['ClubStore', 'ProfileStore', 'UrlStore']
			: ['ClubStore', 'ClubsStore', 'ProfileStore', 'UrlStore'];

		this.getFrameworkData(this.completeMounting, stores);

		// Override overflow: hidden on member site to prevent multiselects getting cut off at footer
		if (document.getElementsByClassName('main-mylt').length > 0) {
			document.getElementsByClassName('main-mylt')[0].style.overflow = 'visible';
		}
	},
	methods: {
		completeMounting() {
			this.club = StateService.getLocationName();

			this.isWaiting = true;
			this.filterTypes = [
				FilterTypes.startDate,
				FilterTypes.endDate
			];

			if (!FrameworkService.isWorkExperience()) {
				this.filterTypes.push(FilterTypes.interestFormat);
				this.filterTypes.push(FilterTypes.age);
				this.filterTypes.push(FilterTypes.location);
				this.filterTypes.push(FilterTypes.freeOnlyEvents);
			}

			this.queryFilters = FilteringService.getSelectedFiltersFromQuery(this.filterTypes);
			this.searchFilters = FilteringService.getInitialFilters(FilteringServicePages.events, this.queryFilters, this.filterTypes);
			this.searchChiclets = this.searchFilters.chiclets;
			this.loadEvents();
		},

		loadEvents() {
			this.getEvents();
			this.getNearbyEvents();
		},

		getEvents() {
			UxSchedulesApi
				.getEvents(0, this.searchFilters)
				.then(response => {
					if (!response.ok) {
						this.isWaiting = false;
						return this.mountServerError(true, response.data.error);
					}

					this.events = response.data.results;
					this.setupEventsData(this.events);

					this.facetData = response.data.filters;
					this.show = true;
					this.isWaiting = false;

					this.$nextTick(()=> {
						$('#filtersSub').on('show.bs.collapse', this.handleFiltersShow);
						$('#filtersSub').on('hide.bs.collapse', this.handleFiltersHide);
					});
				});
		},

		getNearbyEvents() {
			UxSchedulesApi
				.getNearbyEvents(this.searchFilters)
				.then(response => {
					if (!response.ok) {
						return this.mountServerError(true, response.data.error);
					}

					this.nearbyEvents = response.data.results;
					this.setupEventsData(this.nearbyEvents);

					if (this.nearbyEvents.length) {
						this.nearbyEventsVisible = true;
					} else {
						this.nearbyEventsVisible = false;
					}
				});
		},

		setupEventsData(events) {
			_.forEach(events, (event) => {
				event.startMoment = moment.parseZone(event.start, moment.ISO_8601);
				event.endMoment = moment.parseZone(event.end, moment.ISO_8601);
				event.dateFull = event.startMoment.format('dddd, MMM D');
				event.dateDayOfWeekAbbr = event.startMoment.format('ddd');
				event.dateDayOfMonth = event.startMoment.format('D');
				event.dateMonthAbbr = event.startMoment.format('MMM');
				event.url = FrameworkService.eventUrl(event.id);
				event.imageUrl = event.featured ? `url(${event.imageUrl})` : 'none';
				event.distance = event.distance.toFixed(1);
			});
		},

		handleFiltersShow() {
			StateService.setQueryStringKey('showFilters', true);
		},

		handleFiltersHide() {
			StateService.setQueryStringKey('showFilters', false);
		},

		handleRebuildQueryFilters() {
			// Validate the filters passed in the query string
			const invalid = this.$refs.filtersSub.validateQueryValues(this.queryFilters);
			if (invalid.length > 0) {
				const chics = [];
				for (const chic of this.searchChiclets) {
					// Add valid filters from the query string to the chiclet bar
					if (!invalid.includes(chic.value)) {
						chics.push(chic);
					}
				}
				this.searchChiclets = chics;
			}
			this.queryFilters = [];

			// Remove everything from the query string
			for (const filterType of this.filterTypes) {
				StateService.removeQueryStringKey(filterType);
			}

			// Rebuild the query string
			this.$nextTick(() => {
				const query = this.$refs.filtersSub.getQueryStringValues();
				for (const item of query) {
					StateService.setQueryStringKey(item.name, item.values);
				}
			});

			if (StateService.getQuery('showFilters')) $('#filtersSub').collapse('show');
		},

		handleRemovedChiclet(chiclet) {
			this.removeChiclet(chiclet);
		},

		handleClearAllChiclets() {
			for (const filterType of this.filterTypes) {
				if (filterType !== FilterTypes.startDate && filterType !== FilterTypes.endDate) {
					StateService.removeQueryStringKey(filterType);
				}
			}
			window.location.reload();
		},

		removeChiclet(chiclet) {
			this.$refs.filtersSub.deselectFilterValue(chiclet.type, chiclet.value);
		},

		handleFiltersChanged(updatedFilters) {
			this.isWaiting = true;
			this.searchFilters = FilteringService.getUpdatedFilters(FilteringServicePages.events, updatedFilters, this.filterTypes);
			this.searchChiclets = this.searchFilters.chiclets;
			this.loadEvents();
		}
	}
});
</script>

<style lang="scss" scoped>
@import "@/common/styles/panels.scss";
@import "@/common/styles/titleSection.scss";
@import "@/common/styles/loading.scss";
@import "styles/events.scss";
</style>
